import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Trash2, Shield, Package, Ban, Play, Users as UsersIcon, CreditCard, AlertCircle, CheckCircle2, Clock, TrendingUp, FileArchive, FileText, Loader2 } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const STATUS_STYLES = {
  trial: { label: "Trial", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  active: { label: "Attivo", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  past_due: { label: "In ritardo", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  suspended: { label: "Sospeso", cls: "bg-red-100 text-red-800 border-red-200" },
  canceled: { label: "Cancellato", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

// ISO alpha-2 country code -> flag emoji (regional indicator symbols)
function countryFlag(iso2) {
  if (!iso2 || iso2.length !== 2) return "🌍";
  const cc = iso2.toUpperCase();
  const A = 0x1F1E6;
  return String.fromCodePoint(A + (cc.charCodeAt(0) - 65), A + (cc.charCodeAt(1) - 65));
}

// ISO alpha-2 -> nome paese in italiano (solo i più comuni; fallback = codice)
const COUNTRY_NAMES_IT = {
  IT: "Italia", DE: "Germania", FR: "Francia", ES: "Spagna", GB: "Regno Unito",
  US: "Stati Uniti", CH: "Svizzera", AT: "Austria", NL: "Paesi Bassi", BE: "Belgio",
  PT: "Portogallo", GR: "Grecia", IE: "Irlanda", SE: "Svezia", NO: "Norvegia",
  DK: "Danimarca", FI: "Finlandia", PL: "Polonia", RO: "Romania", CZ: "Cechia",
  HU: "Ungheria", SK: "Slovacchia", SI: "Slovenia", HR: "Croazia", BG: "Bulgaria",
  RS: "Serbia", AL: "Albania", TR: "Turchia", RU: "Russia", UA: "Ucraina",
  CA: "Canada", MX: "Messico", BR: "Brasile", AR: "Argentina", CL: "Cile",
  CN: "Cina", JP: "Giappone", KR: "Corea del Sud", IN: "India", AU: "Australia",
  NZ: "Nuova Zelanda", ZA: "Sudafrica", EG: "Egitto", MA: "Marocco", TN: "Tunisia",
  AE: "Emirati Arabi", SA: "Arabia Saudita", IL: "Israele",
};
function countryName(iso2) {
  if (!iso2) return "Sconosciuto";
  return COUNTRY_NAMES_IT[iso2.toUpperCase()] || iso2.toUpperCase();
}

export default function SuperAdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("tenants");

  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsRange, setAnalyticsRange] = useState(30);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [openPlan, setOpenPlan] = useState(false);
  const [planForm, setPlanForm] = useState({ code: "", name: "", description: "", price_eur: 29, features_csv: "", max_technicians: "" });
  const [landingCfg, setLandingCfg] = useState({ video_url: "", video_title: "Guarda come funziona in 3 minuti", video_description: "" });
  const [landingSaving, setLandingSaving] = useState(false);

  const [openTenant, setOpenTenant] = useState(false);
  const [tenantForm, setTenantForm] = useState({ company_name: "", admin_name: "", admin_email: "", admin_password: "", plan_id: "", trial_days: 7 });

  const load = async () => {
    setLoading(true);
    try {
      const [p, t, m] = await Promise.all([
        api.get("/super-admin/plans"),
        api.get("/super-admin/tenants"),
        api.get("/super-admin/metrics", { params: { days: 90 } }),
      ]);
      setPlans(p.data);
      setTenants(t.data);
      setMetrics(m.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadAnalytics = async (days = analyticsRange) => {
    setAnalyticsLoading(true);
    try {
      const { data } = await api.get("/super-admin/analytics", { params: { days } });
      setAnalytics(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setAnalyticsLoading(false); }
  };

  useEffect(() => {
    if (tab === "analytics" && !analytics) loadAnalytics(analyticsRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const createPlan = async () => {
    if (!planForm.code || !planForm.name || !planForm.price_eur) return toast.error("Compila codice, nome, prezzo");
    try {
      await api.post("/super-admin/plans", {
        code: planForm.code,
        name: planForm.name,
        description: planForm.description,
        price_eur: parseFloat(planForm.price_eur),
        features: planForm.features_csv.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean),
        max_technicians: planForm.max_technicians ? parseInt(planForm.max_technicians) : null,
      });
      toast.success("Piano creato e sincronizzato su Stripe");
      setOpenPlan(false);
      setPlanForm({ code: "", name: "", description: "", price_eur: 29, features_csv: "", max_technicians: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deletePlan = async (id, name) => {
    if (!window.confirm(`Eliminare il piano "${name}"? I tenant già collegati NON vengono toccati.`)) return;
    try { await api.delete(`/super-admin/plans/${id}`); toast.success("Piano eliminato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const togglePlanActive = async (p) => {
    try { await api.patch(`/super-admin/plans/${p.id}`, { is_active: !p.is_active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createTenant = async () => {
    if (!tenantForm.company_name || !tenantForm.admin_email || !tenantForm.admin_password) return toast.error("Compila azienda, email e password admin");
    try {
      const res = await api.post("/super-admin/tenants", {
        company_name: tenantForm.company_name,
        admin_name: tenantForm.admin_name || tenantForm.admin_email,
        admin_email: tenantForm.admin_email,
        admin_password: tenantForm.admin_password,
        plan_id: tenantForm.plan_id || null,
        trial_days: parseInt(tenantForm.trial_days) || 7,
      });
      toast.success(`Azienda creata — trial fino a ${new Date(res.data.trial_ends_at).toLocaleDateString("it-IT")}`);
      setOpenTenant(false);
      setTenantForm({ company_name: "", admin_name: "", admin_email: "", admin_password: "", plan_id: "", trial_days: 7 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const suspendTenant = async (t) => {
    const reason = window.prompt(`Sospendere "${t.name}"? (motivo opzionale)`, "Mancato pagamento");
    if (reason === null) return;
    try { await api.post(`/super-admin/tenants/${t.id}/suspend`, { reason }); toast.success("Tenant sospeso"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reactivateTenant = async (t) => {
    if (!window.confirm(`Riattivare "${t.name}"? Potrà usare di nuovo il software.`)) return;
    try { await api.post(`/super-admin/tenants/${t.id}/reactivate`); toast.success("Tenant riattivato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setPlan = async (t, planId) => {
    try { await api.post(`/super-admin/tenants/${t.id}/set-plan?plan_id=${planId}`); toast.success("Piano aggiornato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteTenant = async (t) => {
    const first = window.prompt(`⚠️ ATTENZIONE: stai per ELIMINARE definitivamente "${t.name}" e TUTTI i suoi dati (utenti, magazzino, lavorazioni, bolle, ecc.).\n\nQuesta azione è IRREVERSIBILE.\n\nScrivi il nome del tenant per confermare:`);
    if (first === null) return;
    if (first.trim() !== (t.name || "").trim()) {
      toast.error("Nome non corrispondente. Eliminazione annullata.");
      return;
    }
    if (!window.confirm(`Ultima conferma: eliminare "${t.name}"?`)) return;
    try {
      await api.delete(`/super-admin/tenants/${t.id}`);
      toast.success(`Tenant "${t.name}" eliminato definitivamente`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore eliminazione");
    }
  };

  const totalActive = tenants.filter((tech) => tech.subscription_status === "active").length;
  const totalTrial = tenants.filter((tech) => tech.subscription_status === "trial").length;
  const totalSuspended = tenants.filter((tech) => tech.subscription_status === "suspended").length;
  const monthlyRevenue = tenants
    .filter((tech) => tech.subscription_status === "active" && tech.subscription_plan_price)
    .reduce((a, tech) => a + Number(tech.subscription_plan_price || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-[#0052FF]" />
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Pannello Master</div>
            <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">Super Admin</h1>
            <p className="mt-0.5 text-sm text-slate-500">{t("auto.gestione_multiazienda_piani_e_abbonamenti_stripe")}</p>
          </div>
        </div>
        <RegistrationDocs />
      </div>

      {/* Metriche */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Attive</div>
          <div className="mt-1 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <span className="text-2xl font-bold text-slate-900">{totalActive}</span>
          </div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">In prova</div>
          <div className="mt-1 flex items-center gap-2">
            <Clock className="h-5 w-5 text-sky-600" />
            <span className="text-2xl font-bold text-slate-900">{totalTrial}</span>
          </div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Sospese</div>
          <div className="mt-1 flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-600" />
            <span className="text-2xl font-bold text-slate-900">{totalSuspended}</span>
          </div>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">MRR stimato</div>
          <div className="mt-1 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-[#0052FF]" />
            <span className="text-2xl font-bold text-slate-900">€ {monthlyRevenue.toFixed(0)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[["tenants", "Aziende"], ["plans", "Piani"], ["metrics", "Metriche"], ["analytics", "Visite sito"], ["landing", "Landing"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`sa-tab-${k}`}
            className={`border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 hover:text-slate-900"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "metrics" && metrics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">MRR</div>
              <div className="mt-1 text-2xl font-bold text-[#0052FF]">€ {Number(metrics.mrr || 0).toFixed(0)}</div>
              <div className="text-[10px] text-slate-500">Ricavi ricorrenti mensili</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Churn 30gg</div>
              <div className="mt-1 text-2xl font-bold text-red-600">{metrics.churn_rate}%</div>
              <div className="text-[10px] text-slate-500">Sospesi ultimi 30 giorni</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Totale aziende</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{metrics.total_tenants}</div>
            </div>
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">ARR proiettato</div>
              <div className="mt-1 text-2xl font-bold text-emerald-600">€ {(Number(metrics.mrr || 0) * 12).toFixed(0)}</div>
              <div className="text-[10px] text-slate-500">MRR × 12</div>
            </div>
          </div>

          <div className="rounded-sm border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#0052FF]" />
              <h3 className="text-sm font-bold text-slate-900">Crescita clienti (90 giorni)</h3>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={metrics.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => v} labelFormatter={(l) => new Date(l).toLocaleDateString("it-IT")} />
                <Area type="monotone" dataKey="cum_tenants" stroke="#0052FF" fill="#0052FF" fillOpacity={0.15} name="Totale aziende" />
                <Area type="monotone" dataKey="signups" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="Nuove registrazioni" />
                <Area type="monotone" dataKey="suspensions" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="Sospensioni" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {(metrics.revenue_by_plan || []).length > 0 && (
            <div className="rounded-sm border border-slate-200 bg-white p-4">
              <h3 className="mb-3 text-sm font-bold text-slate-900">{t("auto.ricavi_per_piano_mrr")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={metrics.revenue_by_plan}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="plan" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => `€ ${Number(v).toFixed(0)}`} />
                  <Bar dataKey="revenue" fill="#0052FF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {tab === "analytics" && (
        <div className="space-y-6" data-testid="sa-analytics">
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Intervallo:</Label>
            <Select value={String(analyticsRange)} onValueChange={(v) => { setAnalyticsRange(Number(v)); loadAnalytics(Number(v)); }}>
              <SelectTrigger className="h-8 w-40 rounded-sm text-xs" data-testid="analytics-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                <SelectItem value="365">Ultimi 12 mesi</SelectItem>
              </SelectContent>
            </Select>
            {analyticsLoading && <span className="text-xs text-slate-400">Caricamento…</span>}
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[10px] text-slate-400">Gli utenti loggati non vengono conteggiati.</span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs text-red-600 hover:bg-red-50" data-testid="analytics-reset">
                    <Trash2 className="mr-1 h-3 w-3" /> Azzera storico
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancellare tutte le visite storiche?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Utile se vuoi ripartire da dati puliti (senza le visite dei tecnici/admin loggati registrate in passato). L'operazione è irreversibile.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={async () => {
                        try {
                          const { data } = await api.delete("/super-admin/analytics");
                          toast.success(`Storico azzerato (${data.deleted} visite eliminate)`);
                          setAnalytics(null);
                          loadAnalytics(analyticsRange);
                        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                      }}
                    >Azzera</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {analytics && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: "Oggi", views: analytics.totals.today, uniq: analytics.uniques.today, color: "text-[#0052FF]" },
                  { label: "Ieri", views: analytics.totals.yesterday, uniq: null, color: "text-slate-900" },
                  { label: "Ultimi 7 giorni", views: analytics.totals.last_7d, uniq: analytics.uniques.last_7d, color: "text-slate-900" },
                  { label: "Ultimi 30 giorni", views: analytics.totals.last_30d, uniq: analytics.uniques.last_30d, color: "text-slate-900" },
                ].map((c) => (
                  <div key={c.label} className="rounded-sm border border-slate-200 bg-white p-4" data-testid={`sa-visit-card-${c.label}`}>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.label}</div>
                    <div className={`mt-1 text-2xl font-bold ${c.color}`}>{c.views.toLocaleString()}</div>
                    {c.uniq != null && <div className="text-[10px] text-slate-500">{c.uniq.toLocaleString()} visitatori unici</div>}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Totale (all time)</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{analytics.totals.all_time.toLocaleString()}</div>
                  <div className="text-[10px] text-slate-500">{analytics.uniques.all_time.toLocaleString()} visitatori unici</div>
                </div>
                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Da mobile</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">
                    {(analytics.devices.find((d) => d.device === "mobile")?.views || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-slate-500">ultimi 30 giorni</div>
                </div>
                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Da desktop</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">
                    {(analytics.devices.find((d) => d.device === "desktop")?.views || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-slate-500">ultimi 30 giorni</div>
                </div>
                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Paesi</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{analytics.countries.length}</div>
                  <div className="text-[10px] text-slate-500">nei 30 giorni (via IP)</div>
                </div>
              </div>

              <div className="rounded-sm border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-[#0052FF]" />
                  <h3 className="text-sm font-bold text-slate-900">Visite giornaliere · {analytics.range_days} giorni</h3>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={analytics.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip labelFormatter={(l) => new Date(l).toLocaleDateString()} />
                    <Area type="monotone" dataKey="views" stroke="#0052FF" fill="#0052FF" fillOpacity={0.18} name="Visualizzazioni" />
                    <Area type="monotone" dataKey="unique_visitors" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="Visitatori unici" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-bold text-slate-900">Pagine più viste (30 giorni)</h3>
                  {analytics.top_pages.length === 0 ? (
                    <div className="text-xs text-slate-400">Nessun dato.</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-slate-200 text-left uppercase text-slate-500"><th className="p-2">Path</th><th className="p-2 text-right">Views</th><th className="p-2 text-right">Unici</th></tr></thead>
                      <tbody>
                        {analytics.top_pages.map((p, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="p-2 font-mono">{p.path}</td>
                            <td className="p-2 text-right font-semibold">{p.views}</td>
                            <td className="p-2 text-right text-slate-500">{p.unique_visitors}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-bold text-slate-900">Provenienza (referrer)</h3>
                  {analytics.top_referrers.length === 0 ? (
                    <div className="text-xs text-slate-400">Nessun referrer registrato (visite dirette).</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-slate-200 text-left uppercase text-slate-500"><th className="p-2">Sorgente</th><th className="p-2 text-right">Views</th></tr></thead>
                      <tbody>
                        {analytics.top_referrers.map((r, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="p-2 truncate max-w-[280px]" title={r.referrer}>{r.referrer}</td>
                            <td className="p-2 text-right font-semibold">{r.views}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-bold text-slate-900">Paesi</h3>
                  {analytics.countries.length === 0 ? (
                    <div className="text-xs text-slate-400">Nessun dato geografico ancora disponibile.</div>
                  ) : (
                    <table className="w-full text-xs" data-testid="analytics-countries-table">
                      <thead><tr className="border-b border-slate-200 text-left uppercase text-slate-500"><th className="p-2">Paese</th><th className="p-2 text-right">Views</th><th className="p-2 text-right">Unici</th></tr></thead>
                      <tbody>
                        {analytics.countries.map((c, i) => (
                          <tr key={i} className="border-b border-slate-100" data-testid={`analytics-country-row-${c.country}`}>
                            <td className="p-2">
                              <span className="mr-2 text-base">{countryFlag(c.country)}</span>
                              {countryName(c.country)}
                              <span className="ml-1 text-slate-400">({c.country})</span>
                            </td>
                            <td className="p-2 text-right font-semibold">{c.views}</td>
                            <td className="p-2 text-right text-slate-500">{c.unique_visitors}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-bold text-slate-900">Città più attive</h3>
                  {(!analytics.cities || analytics.cities.length === 0) ? (
                    <div className="text-xs text-slate-400">Nessun dato città ancora disponibile.</div>
                  ) : (
                    <table className="w-full text-xs" data-testid="analytics-cities-table">
                      <thead><tr className="border-b border-slate-200 text-left uppercase text-slate-500"><th className="p-2">Città</th><th className="p-2 text-right">Views</th><th className="p-2 text-right">Unici</th></tr></thead>
                      <tbody>
                        {analytics.cities.map((c, i) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="p-2">
                              <span className="mr-2">{countryFlag(c.country)}</span>
                              {c.city}
                              {c.country && <span className="ml-1 text-slate-400">· {countryName(c.country)}</span>}
                            </td>
                            <td className="p-2 text-right font-semibold">{c.views}</td>
                            <td className="p-2 text-right text-slate-500">{c.unique_visitors}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="rounded-sm border border-slate-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-bold text-slate-900">Dispositivo</h3>
                  {analytics.devices.length === 0 ? (
                    <div className="text-xs text-slate-400">Nessun dato.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={analytics.devices}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="device" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="views" fill="#0052FF" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {tab === "tenants" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Dialog open={openTenant} onOpenChange={setOpenTenant}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-tenant">
                  <Plus className="mr-2 h-4 w-4" /> Nuova azienda
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Onboarding nuova azienda</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                  <div><Label>Nome azienda *</Label><Input value={tenantForm.company_name} onChange={(e) => setTenantForm({ ...tenantForm, company_name: e.target.value })} className="rounded-sm" data-testid="tenant-company" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Nome admin</Label><Input value={tenantForm.admin_name} onChange={(e) => setTenantForm({ ...tenantForm, admin_name: e.target.value })} className="rounded-sm" data-testid="tenant-admin-name" /></div>
                    <div><Label>Email admin *</Label><Input type="email" value={tenantForm.admin_email} onChange={(e) => setTenantForm({ ...tenantForm, admin_email: e.target.value })} className="rounded-sm font-mono" data-testid="tenant-admin-email" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Password admin *</Label><Input type="password" value={tenantForm.admin_password} onChange={(e) => setTenantForm({ ...tenantForm, admin_password: e.target.value })} className="rounded-sm font-mono" data-testid="tenant-admin-pw" /></div>
                    <div>
                      <Label>Trial (giorni)</Label>
                      <Input type="number" value={tenantForm.trial_days} onChange={(e) => setTenantForm({ ...tenantForm, trial_days: e.target.value })} className="rounded-sm" data-testid="tenant-trial-days" />
                    </div>
                  </div>
                  <div>
                    <Label>Piano</Label>
                    <Select value={tenantForm.plan_id || "__none__"} onValueChange={(v) => setTenantForm({ ...tenantForm, plan_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="rounded-sm" data-testid="tenant-plan"><SelectValue placeholder={t("auto.nessuno")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Nessun piano (imposta dopo) —</SelectItem>
                        {plans.filter((p) => p.is_active).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — €{p.price_eur}/mese</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createTenant} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="tenant-create">Crea</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="p-3">Azienda</th>
                  <th className="p-3">Admin</th>
                  <th className="p-3">Utenti</th>
                  <th className="p-3">Piano</th>
                  <th className="p-3">{t("common.status")}</th>
                  <th className="p-3">Scadenza</th>
                  <th className="p-3 text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">{t("auto.nessuna_azienda")}</td></tr>}
                {tenants.map((tech) => {
                  const st = STATUS_STYLES[tech.subscription_status] || STATUS_STYLES.trial;
                  const endDate = tech.current_period_end || tech.trial_ends_at;
                  return (
                    <tr key={tech.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tenant-row-${tech.id}`}>
                      <td className="p-3">
                        <div className="font-semibold text-slate-900">{tech.name}</div>
                        <div className="text-[10px] font-mono text-slate-400">{tech.id}</div>
                      </td>
                      <td className="p-3">
                        <div>{tech.admin_name || "—"}</div>
                        <div className="text-xs text-slate-500">{tech.admin_email}</div>
                      </td>
                      <td className="p-3 text-xs">
                        <div><UsersIcon className="inline h-3 w-3 mr-1" />{tech.users_count} · <b>{tech.technicians_count}</b> tec</div>
                      </td>
                      <td className="p-3 text-xs">
                        <Select value={tech.subscription_plan_id || "__none__"} onValueChange={(v) => v !== "__none__" && setPlan(tech, v)}>
                          <SelectTrigger className="w-44 rounded-sm text-xs" data-testid={`tenant-plan-select-${tech.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                            {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · €{p.price_eur}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        <span className={`inline-block rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="p-3 text-xs font-mono">
                        {endDate ? new Date(endDate).toLocaleDateString("it-IT") : "—"}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          {tech.subscription_status === "suspended" ? (
                            <Button size="sm" onClick={() => reactivateTenant(tech)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid={`tenant-reactivate-${tech.id}`}>
                              <Play className="mr-1 h-3 w-3" /> Riattiva
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => suspendTenant(tech)} className="rounded-sm text-red-600 hover:bg-red-50" data-testid={`tenant-suspend-${tech.id}`}>
                              <Ban className="mr-1 h-3 w-3" /> Sospendi
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteTenant(tech)}
                            className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                            data-testid={`tenant-delete-${tech.id}`}
                            title="Elimina definitivamente il tenant e tutti i suoi dati"
                          >
                            <Trash2 className="mr-1 h-3 w-3" /> {t("auto.elimina")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "plans" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Dialog open={openPlan} onOpenChange={setOpenPlan}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-plan">
                  <Plus className="mr-2 h-4 w-4" /> Nuovo piano
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo piano abbonamento</DialogTitle></DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                    Il piano verrà sincronizzato automaticamente su Stripe come Product + Price mensile in EUR.
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Codice *</Label><Input value={planForm.code} onChange={(e) => setPlanForm({ ...planForm, code: e.target.value.toLowerCase().replace(/\s+/g, "_") })} placeholder="base" className="rounded-sm font-mono" data-testid="plan-code" /></div>
                    <div><Label>Nome *</Label><Input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Base" className="rounded-sm" data-testid="plan-name" /></div>
                  </div>
                  <div><Label>{t("common.description")}</Label><Input value={planForm.description} onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })} className="rounded-sm" data-testid="plan-desc" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Prezzo (€/mese) *</Label><Input type="number" step="0.01" value={planForm.price_eur} onChange={(e) => setPlanForm({ ...planForm, price_eur: e.target.value })} className="rounded-sm font-mono" data-testid="plan-price" /></div>
                    <div><Label>Max tecnici</Label><Input type="number" value={planForm.max_technicians} onChange={(e) => setPlanForm({ ...planForm, max_technicians: e.target.value })} placeholder="Illimitato" className="rounded-sm font-mono" data-testid="plan-max-tec" /></div>
                  </div>
                  <div>
                    <Label>Funzionalità (separate da virgola o a capo)</Label>
                    <textarea value={planForm.features_csv} onChange={(e) => setPlanForm({ ...planForm, features_csv: e.target.value })} rows={3} className="w-full rounded-sm border border-slate-200 p-2 text-sm" data-testid="plan-features" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createPlan} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="plan-create">Crea</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((p) => (
              <div key={p.id} className="rounded-sm border border-slate-200 bg-white p-4" data-testid={`plan-card-${p.id}`}>
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div>
                    <div className="text-lg font-bold text-slate-900">{p.name}</div>
                    <div className="text-xs font-mono text-slate-400">{p.code}</div>
                  </div>
                  <Badge className={p.is_active ? "bg-emerald-600" : "bg-slate-400"}>{p.is_active ? "Attivo" : "Off"}</Badge>
                </div>
                <div className="text-3xl font-bold text-[#0052FF]">€ {Number(p.price_eur).toFixed(0)}<span className="text-sm font-normal text-slate-500">/mese</span></div>
                {p.description && <div className="mt-2 text-xs text-slate-600">{p.description}</div>}
                {p.max_technicians && <div className="mt-1 text-xs text-slate-500">Max {p.max_technicians} tecnici</div>}
                {(p.features || []).length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-slate-700">
                    {p.features.map((f, i) => <li key={i}>✓ {f}</li>)}
                  </ul>
                )}
                <div className="mt-4 flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => togglePlanActive(p)} className="rounded-sm flex-1" data-testid={`plan-toggle-${p.id}`}>
                    {p.is_active ? "Disattiva" : "Attiva"}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deletePlan(p.id, p.name)} data-testid={`plan-del-${p.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "landing" && (
        <LandingTab
          cfg={landingCfg}
          setCfg={setLandingCfg}
          saving={landingSaving}
          onLoad={async () => {
            try {
              const { data } = await api.get("/super-admin/landing-config");
              setLandingCfg(data);
            } catch (e) { /* ignore */ }
          }}
          onSave={async () => {
            setLandingSaving(true);
            try {
              const { data } = await api.put("/super-admin/landing-config", landingCfg);
              setLandingCfg(data);
              toast.success("Landing aggiornata");
            } catch (e) { toast.error(t("common.error")); }
            finally { setLandingSaving(false); }
          }}
        />
      )}
    </div>
  );
}

function LandingTab({ cfg, setCfg, saving, onLoad, onSave }) {
  const { t } = useTranslation();
  useEffect(() => { onLoad(); /* eslint-disable-next-line */ }, []);

  // Preview embed
  const url = cfg.video_url || "";
  const embed = (() => {
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
      if (u.hostname.includes("youtube.com")) {
        const v = u.searchParams.get("v");
        if (v) return `https://www.youtube.com/embed/${v}`;
        if (u.pathname.startsWith("/embed/")) return url;
      }
      if (u.hostname.includes("loom.com")) return url.replace("/share/", "/embed/");
      if (u.hostname.includes("vimeo.com")) {
        const id = u.pathname.split("/").filter(Boolean).pop();
        if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
      }
      return null;
    } catch { return null; }
  })();

  return (
    <div className="space-y-4">
      <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="landing-config-card">
        <div className="mb-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Video demo sulla landing pubblica</div>
          <div className="mt-1 text-xs text-slate-500">Incolla un link YouTube, Loom o Vimeo. Apparirà sopra i piani su <code className="rounded-sm bg-slate-100 px-1">/signup</code>.</div>
        </div>
        <div className="space-y-3">
          <div>
            <Label>URL video</Label>
            <Input
              value={cfg.video_url || ""}
              onChange={(e) => setCfg({ ...cfg, video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=... oppure https://www.loom.com/share/..."
              className="rounded-sm font-mono text-xs"
              data-testid="landing-video-url"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label>Titolo (opzionale)</Label>
              <Input
                value={cfg.video_title || ""}
                onChange={(e) => setCfg({ ...cfg, video_title: e.target.value })}
                placeholder={t("auto.guarda_come_funziona_in_3_minuti")}
                className="rounded-sm"
                data-testid="landing-video-title"
              />
            </div>
            <div>
              <Label>Descrizione breve (opzionale)</Label>
              <Input
                value={cfg.video_description || ""}
                onChange={(e) => setCfg({ ...cfg, video_description: e.target.value })}
                placeholder={t("auto.dal_magazzino_alle_bolle_firmate_tutto_in_un_video")}
                className="rounded-sm"
                data-testid="landing-video-desc"
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-slate-500">
              {url && !embed && <span className="text-amber-700">⚠ URL non riconosciuto (verrà mostrato come link cliccabile).</span>}
              {!url && <span>{t("auto.lascia_vuoto_per_non_mostrare_alcun_video")}</span>}
            </div>
            <Button onClick={onSave} disabled={saving} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="landing-save">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </div>
        </div>
      </div>

      {url && (
        <div className="rounded-sm border border-slate-200 bg-white p-6">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.anteprima")}</div>
          <div className="relative overflow-hidden rounded-sm border border-slate-200 bg-black" style={{ paddingTop: "56.25%" }}>
            {embed ? (
              <iframe src={embed} title={t("auto.anteprima")} className="absolute inset-0 h-full w-full" frameBorder="0" allowFullScreen />
            ) : (
              <a href={url} target="_blank" rel="noopener noreferrer" className="absolute inset-0 grid place-items-center bg-slate-900 text-white text-sm">
                {url}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const REG_DOCS = [
  { kind: "zip", label: "ZIP codice completo", Icon: FileArchive, file: "EVOSOFT_codice_completo.zip" },
  { kind: "descrizione", label: "PDF descrizione", Icon: FileText, file: "EVOSOFT_descrizione_software.pdf" },
  { kind: "sorgente", label: "PDF codice sorgente", Icon: FileText, file: "EVOSOFT_codice_sorgente.pdf" },
];

function RegistrationDocs() {
  const [busy, setBusy] = useState("");

  const download = async ({ kind, file, label }) => {
    setBusy(kind);
    try {
      toast.info("Aggiornamento documenti in corso, può richiedere fino a un minuto…");
      await api.post("/super-admin/registration-docs/regenerate", {}, { timeout: 300000 });
      const r = await api.get(`/super-admin/registration-docs/${kind}`, { responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = file; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} scaricato (versione aggiornata)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download non riuscito");
    } finally { setBusy(""); }
  };

  return (
    <div className="rounded-sm border border-slate-200 bg-white p-3" data-testid="registration-docs">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Registrazione software</div>
      <div className="flex flex-wrap gap-2">
        {REG_DOCS.map((d) => (
          <Button key={d.kind} size="sm" variant="outline" className="rounded-sm" disabled={!!busy} onClick={() => download(d)} data-testid={`registration-download-${d.kind}`}>
            {busy === d.kind ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <d.Icon className="mr-1.5 h-3.5 w-3.5" />}
            {d.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

